import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import moment from 'moment-jalaali';
import { formatNumberWithPersianSeparators } from '../utils/formatNumber';

const PaymentReceipt = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const parseDescriptionFromURL = useCallback((descParam) => {
    try {
      if (!descParam) {
        console.warn('[PaymentReceipt] No desc parameter provided');
        return null;
      }

      // Decode URL-encoded string
      const decodedDesc = decodeURIComponent(descParam);
      console.log('[PaymentReceipt] Decoded description:', decodedDesc);

      // Split by | to get individual fields
      const fields = decodedDesc.split(' | ');
      
      // Parse each field into key-value pairs
      const parsedData = {};
      
      fields.forEach(field => {
        const colonIndex = field.indexOf(':');
        if (colonIndex !== -1) {
          const key = field.substring(0, colonIndex).trim();
          const value = field.substring(colonIndex + 1).trim();
          parsedData[key] = value;
        }
      });

      console.log('[PaymentReceipt] Parsed appointment data:', parsedData);
      return parsedData;
    } catch (error) {
      console.error('[PaymentReceipt] Error parsing description from URL:', error);
      return null;
    }
  }, []);

  const [invoiceData, setInvoiceData] = useState(() => {
    try {
      const raw = localStorage.getItem('invoiceData');
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      console.error('[PaymentReceipt] Failed to parse invoiceData from localStorage:', e);
      return {};
    }
  });
  const authority = localStorage.getItem('authority');
  const [serviceCategoryId, setServiceCategoryId] = useState(localStorage.getItem('ServiceCategoryID'));
  const [serviceDoctorData] = useState(null);
  const [webServiceDebugText, setWebServiceDebugText] = useState('');
  const [sabtNobatUrl, setSabtNobatUrl] = useState(null);

  // Clears only payment related keys from localStorage
  const clearPaymentStorage = () => {
    try {
      localStorage.removeItem('invoiceData');
      localStorage.removeItem('authority');
      localStorage.removeItem('isPaymentSuccessful');
    } catch (error) {
      console.warn('[PaymentReceipt] Failed to clear payment storage keys:', error);
    }
  };

  // Clear only payment data when user leaves this page (unmount) or refresh/close tab
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Note: synchronous best-effort clear before unload of payment-related keys only
      try {
        localStorage.removeItem('invoiceData');
        localStorage.removeItem('authority');
        localStorage.removeItem('isPaymentSuccessful');
      } catch (e) {}
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Do scoped cleanup on component unmount (navigating away)
      clearPaymentStorage();
    };
  }, []);

  // Function to convert date to Shamsi format
  const convertToShamsi = (date) => {
    if (!date) return 'نامشخص';
    // Try parse as Jalali first, then common Gregorian formats
    const parsed = moment(date, [
      'jYYYY/jMM/jDD',
      'YYYY/MM/DD',
      'YYYY-MM-DD',
      'MM/DD/YYYY',
    ], true);
    if (parsed.isValid()) {
      return parsed.format('jYYYY/jMM/jDD');
    }
    // Fallback to moment auto-parse
    const auto = moment(date);
    return auto.isValid() ? auto.format('jYYYY/jMM/jDD') : 'نامشخص';
  };

  // Function to format time in Shamsi format
  const formatTime = (time) => {
    console.log('[PaymentReceipt] formatTime input time:', time, 'Type:', typeof time);
    if (!time || typeof time !== 'string') {
      console.warn('[PaymentReceipt] formatTime received invalid time input:', time);
      return 'نامشخص';
    }
    const parsed = moment(time, [
      'HH:mm',
      'jYYYY/jMM/jDD HH:mm',
      'YYYY/MM/DD HH:mm:ss',
      'MM/DD/YYYY HH:mm:ss',
    ], true);
    if (parsed.isValid()) {
      return parsed.format('HH:mm');
    }
    // Fallback: try to extract last 5 chars if they look like HH:mm
    const candidate = String(time).slice(-5);
    return /^\d{2}:\d{2}$/.test(candidate) ? candidate : 'نامشخص';
  };

  // (Removed verbose initial logging to avoid unnecessary re-renders and linter warnings)

  // Add useEffect to handle payment data
  useEffect(() => {
    const initializePaymentData = () => {
      console.log('[PaymentReceipt] Initializing Payment Data');
      
      // Check for desc parameter in URL first
      const searchParams = new URLSearchParams(location.search);
      const descParam = searchParams.get('desc');
      const statusFromUrl = searchParams.get('Status') || searchParams.get('status');
      const authorityFromUrl = searchParams.get('Authority') || searchParams.get('authority');
      let parsedAppointmentData = null;
      
      if (descParam) {
        parsedAppointmentData = parseDescriptionFromURL(descParam);
        console.log('[PaymentReceipt] Using parsed appointment data from URL:', parsedAppointmentData);
      }
      
      // Get data from localStorage
      const storedInvoiceData = localStorage.getItem('invoiceData');
      const storedAuthority = localStorage.getItem('authority');
      const storedServiceCategoryId = localStorage.getItem('ServiceCategoryID');
      
      console.log('[PaymentReceipt] Stored Invoice Data:', storedInvoiceData);
      console.log('[PaymentReceipt] Stored Authority:', storedAuthority);
      console.log('[PaymentReceipt] Stored ServiceCategoryID:', storedServiceCategoryId);

      // Use parsed data to enhance or override localStorage data
      if (parsedAppointmentData) {
        let enhancedInvoiceData = {};
        
        if (storedInvoiceData) {
          try {
            enhancedInvoiceData = JSON.parse(storedInvoiceData);
          } catch (error) {
            console.error('[PaymentReceipt] Error parsing stored invoice data:', error);
          }
        }
        
        // Override with parsed data
        enhancedInvoiceData = {
          ...enhancedInvoiceData,
          CategoryName: parsedAppointmentData['Appointment Type'] || enhancedInvoiceData.CategoryName,
          doctorName: parsedAppointmentData['Doctor']?.split(' ').slice(1).join(' ') || enhancedInvoiceData.doctorName,
          doctorFamil: parsedAppointmentData['Doctor']?.split(' ')[0] || enhancedInvoiceData.doctorFamil,
          appointmentDate: parsedAppointmentData['Appointment Date'] || enhancedInvoiceData.appointmentDate,
          appointmentTime: parsedAppointmentData['Appointment Time'] || enhancedInvoiceData.appointmentTime,
          patientName: parsedAppointmentData['Patient Name'] || enhancedInvoiceData.patientName,
          paymentAmount: parsedAppointmentData['Amount']?.replace(' Rials', '') || enhancedInvoiceData.paymentAmount,
          serviceDoctorId: parsedAppointmentData['Service Doctor ID'] || enhancedInvoiceData.serviceDoctorId,
          doctorId: parsedAppointmentData['Doctor ID'] || enhancedInvoiceData.doctorId,
          patientId: parsedAppointmentData['Patient ID'] || enhancedInvoiceData.patientId,
          appointmentNumber: parsedAppointmentData['Appointment Number'] || enhancedInvoiceData.appointmentNumber
        };
        
        console.log('[PaymentReceipt] Enhanced Invoice Data:', enhancedInvoiceData);
        setInvoiceData(enhancedInvoiceData);
        // Prepare web service debug text
        const toHHMMSS = (time) => {
          if (!time) return '';
          const parts = String(time).split(':');
          const hour = (parts[0] || '00').padStart(2, '0');
          const minute = (parts[1] || '00').padStart(2, '0');
          const second = (parts[2] || '00').padStart(2, '0');
          return `${hour}:${minute}:${second}`;
        };
        const gregorianParts = moment(enhancedInvoiceData.appointmentDate, 'jYYYY/jMM/jDD').format('YYYY-MM-DD').split('-');
        const resultTime = `${gregorianParts[1]}/${gregorianParts[2]}/${gregorianParts[0]}`;
        const sabtdate = moment().format('MM/DD/YYYY HH:mm:ss');
        const sabtNobatUrlRuntime = (window.__WS_DEBUG && (window.__WS_DEBUG.lastSabtNobatVerify?.url || window.__WS_DEBUG.lastSabtNobatWallet?.url)) || localStorage.getItem('lastSabtNobatUrl') || null;
        const wsInfo = {
          url: window.location.href,
          status: statusFromUrl || null,
          authority: authorityFromUrl || storedAuthority || null,
          desc: descParam || null,
          parsedFromDesc: parsedAppointmentData || null,
          sabtNobatUrlRuntime: sabtNobatUrlRuntime,
          sabtNobatParams: {
            // SesID is not available on this page
            OID: 1,
            PatientID: enhancedInvoiceData.patientId || null,
            ServiceDoctorID: enhancedInvoiceData.serviceDoctorId || null,
            NobatNumber: enhancedInvoiceData.appointmentNumber || null,
            timadate: toHHMMSS(enhancedInvoiceData.appointmentTime),
            Date: resultTime,
            TransactionNum: authorityFromUrl || storedAuthority || null,
            sabtdate: sabtdate,
          },
          invoiceData: enhancedInvoiceData,
        };
        const composedDebugText = [
          sabtNobatUrlRuntime ? `SabtNobat URL (runtime): ${sabtNobatUrlRuntime}` : 'SabtNobat URL (runtime): <unavailable>',
          '',
          JSON.stringify(wsInfo, null, 2)
        ].join('\n');
        setWebServiceDebugText(composedDebugText);
        setSabtNobatUrl(sabtNobatUrlRuntime);
        
        // Update service category ID from parsed data
        const serviceCategoryIdFromDesc = parsedAppointmentData['Service Category ID'];
        if (serviceCategoryIdFromDesc && serviceCategoryIdFromDesc !== 'Unknown') {
          setServiceCategoryId(serviceCategoryIdFromDesc);
          console.log('[PaymentReceipt] Setting ServiceCategoryID from desc:', serviceCategoryIdFromDesc);
        }
      } else if (storedInvoiceData) {
        try {
          const parsedData = JSON.parse(storedInvoiceData);
          console.log('[PaymentReceipt] Parsed Invoice Data:', parsedData);
          console.log('[PaymentReceipt] Data fields:', {
            CategoryName: parsedData.CategoryName,
            doctorName: parsedData.doctorName,
            doctorFamil: parsedData.doctorFamil,
            appointmentDate: parsedData.appointmentDate,
            appointmentTime: parsedData.appointmentTime,
            patientName: parsedData.patientName,
            paymentAmount: parsedData.paymentAmount,
            paymentTime: parsedData.paymentTime,
            patientId: parsedData.patientId,
          });
          setInvoiceData(parsedData);
          // Prepare web service debug text
          const toHHMMSS = (time) => {
            if (!time) return '';
            const parts = String(time).split(':');
            const hour = (parts[0] || '00').padStart(2, '0');
            const minute = (parts[1] || '00').padStart(2, '0');
            const second = (parts[2] || '00').padStart(2, '0');
            return `${hour}:${minute}:${second}`;
          };
          const gregorianParts = moment(parsedData.appointmentDate, 'jYYYY/jMM/jDD').format('YYYY-MM-DD').split('-');
          const resultTime = `${gregorianParts[1]}/${gregorianParts[2]}/${gregorianParts[0]}`;
          const sabtdate = moment().format('MM/DD/YYYY HH:mm:ss');
          const sabtNobatUrlRuntime = (window.__WS_DEBUG && (window.__WS_DEBUG.lastSabtNobatVerify?.url || window.__WS_DEBUG.lastSabtNobatWallet?.url)) || localStorage.getItem('lastSabtNobatUrl') || null;
          const wsInfo = {
            url: window.location.href,
            status: statusFromUrl || null,
            authority: authorityFromUrl || storedAuthority || null,
            desc: descParam || null,
            parsedFromDesc: null,
            sabtNobatUrlRuntime: sabtNobatUrlRuntime,
            sabtNobatParams: {
              // SesID is not available on this page
              OID: 1,
              PatientID: parsedData.patientId,
              ServiceDoctorID: parsedData.serviceDoctorId || null,
              NobatNumber: parsedData.appointmentNumber || null,
              timadate: toHHMMSS(parsedData.appointmentTime),
              Date: resultTime,
              TransactionNum: authorityFromUrl || storedAuthority || null,
              patientId: parsedData.patientId,
              sabtdate: sabtdate,
            },
            invoiceData: parsedData,
          };
          const composedDebugText = [
            sabtNobatUrlRuntime ? `SabtNobat URL (runtime): ${sabtNobatUrlRuntime}` : 'SabtNobat URL (runtime): <unavailable>',
            '',
            JSON.stringify(wsInfo, null, 2)
          ].join('\n');
          setWebServiceDebugText(composedDebugText);
          setSabtNobatUrl(sabtNobatUrlRuntime);
        } catch (error) {
          console.error('[PaymentReceipt] Error parsing invoice data:', error);
        }
      }

      if (storedServiceCategoryId && !parsedAppointmentData) {
        console.log('[PaymentReceipt] Setting ServiceCategoryID from localStorage:', storedServiceCategoryId);
        setServiceCategoryId(storedServiceCategoryId);
      }

      // If no data is found, redirect to history
      if (!storedInvoiceData && !parsedAppointmentData) {
        console.warn('[PaymentReceipt] No payment data found, redirecting to history');
        navigate('/history');
      }
    };

    initializePaymentData();
  }, [navigate, parseDescriptionFromURL, location.search]);

  // Add console log to debug serviceCategoryId
  useEffect(() => {
    console.log('[PaymentReceipt] Service Category ID Update');
    console.log('Current serviceCategoryId:', serviceCategoryId);
    console.log('Upload button visibility:', {
      serviceCategoryId,
      showUploadButton: serviceCategoryId === '555' || serviceCategoryId === '5556',
      condition: serviceCategoryId === '555' || serviceCategoryId === '5556'
    });
  }, [serviceCategoryId]);

  // Add effect to monitor invoiceData changes
  useEffect(() => {
    console.log('[PaymentReceipt] Invoice Data Update');
    console.log('Current invoiceData:', invoiceData);
  }, [invoiceData]);

  if (!invoiceData || !authority) {
    console.warn('[PaymentReceipt] invoiceData or authority missing');
    return null;
  }

  const handlePrint = () => {
    const printContent = document.createElement('div');
    printContent.innerHTML = `
      <div dir="rtl" style="padding: 20px; font-family: 'Tahoma', sans-serif;">
        <h2 style="text-align: center; margin-bottom: 20px;">فاکتور پرداخت</h2>
        <div style="margin-bottom: 20px;">
          <p><strong>نوبت انتخابی:</strong> ${invoiceData.CategoryName || 'نامشخص'}</p>
          <p><strong>نام پزشک:</strong> ${invoiceData.doctorName && invoiceData.doctorFamil ? `${invoiceData.doctorName}& ${invoiceData.doctorFamil}` : 'نامشخص'}</p>
          <p><strong>تاریخ نوبت:</strong> ${convertToShamsi(invoiceData.appointmentDate)}</p>
          <p><strong>زمان نوبت:</strong> ${formatTime(invoiceData.appointmentTime)}</p>
          <p><strong>نام مراجعه‌کننده:</strong> ${invoiceData.patientName || 'نامشخص'}</p>
          <p><strong>مبلغ پرداختی:</strong> ${invoiceData.paymentAmount ? `${invoiceData.paymentAmount} ریال` : 'نامشخص'}</p>
          <p><strong>زمان پرداخت:</strong> ${moment().format('HH:mm')}</p>
          <p><strong>شماره تراکنش:</strong> <span style="direction:ltr; unicode-bidi:bidi-override; word-break:break-all; overflow-wrap:anywhere; display:inline-block; max-width:100%; vertical-align:bottom;">${authority || 'نامشخص'}</span></p>
          <p><strong>وضعیت:</strong> موفق</p>
        </div>
        <div style="text-align: center; margin-top: 30px; font-size: 12px;">
          <p>این فاکتور به صورت خودکار صادر شده و نیاز به مهر و امضا ندارد.</p>
        </div>
      </div>
    `;
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const handleGoToHistory = () => {
    if (serviceCategoryId === '555' || serviceCategoryId === '5556') {
      localStorage.setItem('ServiceCategoryID', serviceCategoryId);
      navigate('/uploadFiles');
    } else {
      localStorage.removeItem('isPaymentSuccessful');
      localStorage.removeItem('authority');
      localStorage.removeItem('invoiceData');
      navigate('/history');
    }
  };

  // Removed unused upload-related handlers
  
  return (
    <div className="container mt-5">
      <style>
        {`
          @media print {
            body * {
              visibility: hidden !important;
            }
            #print-invoice, #print-invoice * {
              visibility: visible !important;
            }
            #print-invoice {
              position: absolute;
              left: 0;
              top: 0;
              width: 100vw;
              background: #fff !important;
              color: #000 !important;
              box-shadow: none !important;
              padding: 32px !important;
              font-family: Tahoma, Arial, sans-serif !important;
            }
            #print-invoice h3 {
              text-align: center;
              margin-bottom: 24px;
              font-size: 24px;
            }
            #print-invoice p {
              font-size: 16px;
              margin: 8px 0;
            }
            #print-invoice .d-flex,
            #print-invoice button {
              display: none !important;
            }
          }
          .confirm-btn {
            background-color: #0db2ba !important;
            border-color: #0db2ba !important;
          }
          .confirm-btn:hover {
            background-color: #0a9ba2 !important;
            border-color: #0a9ba2 !important;
          }
          /* Ensure long authority code is fully visible and wraps on small screens */
          .authority-value {
            direction: ltr;
            unicode-bidi: bidi-override;
            word-break: break-all;
            overflow-wrap: anywhere;
            display: inline-block;
            max-width: 100%;
            vertical-align: bottom;
          }
        `}
      </style>
      <div className="row mb-5 justify-content-center">
        <div className="col-md-6 doc_col pe-2">
          <div id="print-invoice" className="card p-3 shadow rounded-3 h-100">
            <h3 className="text-center">فاکتور پرداخت</h3>
            <div className="mt-4 text-center">
              <p><strong>نوبت انتخابی شما:</strong> {invoiceData.CategoryName || 'نامشخص'}</p>
              <p><strong>نام پزشک:</strong> {serviceCategoryId === '1' ? 'بینایی سنج' : (invoiceData.doctorName && invoiceData.doctorFamil ? `${invoiceData.doctorName} ${invoiceData.doctorFamil}` : 'نامشخص')}</p>
              <p><strong>تاریخ نوبت:</strong> {convertToShamsi(invoiceData.appointmentDate)}</p>
              <p><strong>زمان نوبت:</strong> {formatTime(invoiceData.appointmentTime)}</p>
              <p><strong>نام مراجعه‌کننده:</strong> {invoiceData.patientName || 'نامشخص'}</p>
              <p><strong>مبلغ پرداختی:</strong> {invoiceData.paymentAmount ? `${formatNumberWithPersianSeparators(invoiceData.paymentAmount)} ریال` : 'نامشخص'}</p>
              <p><strong>زمان پرداخت:</strong> {moment().format('HH:mm')}</p>
              <p>
                <strong>شماره تراکنش (Authority):</strong>
                {' '}
                <span className="authority-value">{authority || 'نامشخص'}</span>~
              </p>
            
              <p><strong>وضعیت:</strong> موفق</p>
            </div>
            <div className="d-flex gap-2 mt-4 justify-content-center">
              <Button variant="danger" onClick={handlePrint}>پرینت</Button>
              <Button variant="primary" className="confirm-btn" onClick={handleGoToHistory}>گزارش نوبت </Button>
              {/* {showUploadButton && (
                <Button variant="success" onClick={handleGoToUpload}>آپلود اطلاعات</Button>
              )} */}
            </div>
            {serviceDoctorData && (
              <pre style={{ direction: 'ltr', textAlign: 'left', background: '#eee', padding: 10, borderRadius: 5 }}>
                {JSON.stringify(serviceDoctorData, null, 2)}
              </pre>
            )}
          </div>
        </div>
        <div className="col-md-6 ps-2">
          <div className="card p-3 shadow rounded-3 h-100 text-center">
            <img
              src={'/Img/printer.png'}
              alt="wallet"
              className="w-100 h-100"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentReceipt; 